import { useEffect, useRef, useState, useCallback } from 'preact/hooks'
import { sendingDomainsApi } from '@/api/sendingDomains'
import type { SendingDomainViewDto } from '@/types/sendingDomains'

/** Loads + maintains the list of sending domains for a client company. */
export function useSendingDomains(clientCompanyId: string) {
  const [domains, setDomains] = useState<SendingDomainViewDto[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Generation token: only the most recent load applies its result, so a superseded
  // (clientCompanyId changed) or unmounted load can't write stale data.
  const requestIdRef = useRef(0)

  const load = useCallback(async () => {
    if (!clientCompanyId) return
    const reqId = ++requestIdRef.current
    setLoading(true)
    setError(null)
    const res = await sendingDomainsApi.list(clientCompanyId)
    if (requestIdRef.current !== reqId) return
    if (res.error) setError(res.error)
    if (res.data) setDomains(res.data)
    setLoading(false)
  }, [clientCompanyId])

  useEffect(() => {
    void load()
    // Invalidate any in-flight load on unmount / clientCompanyId change.
    return () => { requestIdRef.current++ }
  }, [load])

  const upsert = (domain: SendingDomainViewDto) =>
    setDomains(prev => {
      if (!prev) return [domain]
      const idx = prev.findIndex(d => d.Id === domain.Id)
      if (idx === -1) return [domain, ...prev]
      const next = prev.slice()
      next[idx] = domain
      return next
    })

  const remove = (id: string) => setDomains(prev => prev?.filter(d => d.Id !== id) ?? null)

  return { domains, loading, error, load, upsert, remove }
}
